import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import PlusSquare from "@insite/mobius/Icons/PlusSquare";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { EventRegistrationModel } from "@tCooneyBrothers/ApiModels";
import { formatPhoneNumber } from "@tCooneyBrothers/Common/Utilities/formatPhoneNumber";
import { CallbackType, ErrorObjectType, InputObjectType, validate } from "@tCooneyBrothers/Common/Utilities/validate";
import { CreateAccountExistingFormStyles } from "@tCooneyBrothers/Components/CreateAccountExistingForm";
import {
    eventRegistrationValidatorConfig,
    FormErrorsStateType,
    intialErrorFormState,
    ValidatorConfigType,
} from "@tCooneyBrothers/Components/EventRegistrationFormValidatorConfig";
import addEventRegistrationFormFields from "@tCooneyBrothers/Handlers/EventRegistrationFormHandler";
import loadUserDetails from "@tCooneyBrothers/Handlers/LoadUserDetails";
import CustomApplicationState from "@tCooneyBrothers/Store/CustomApplicationState";
import parse from "html-react-parser";
import React, { FC, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface EventRegistrationFormStyles {
    wrapper?: InjectableCss;
    sectionHeading?: InjectableCss;
    redAstick?: InjectableCss;
    sectionLabel?: InjectableCss;
    section?: InjectableCss;
    registerSection?: InjectableCss;
    sectionWrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    gridItem?: GridItemProps;
    paymentMethodSelect?: SelectPresentationProps;
    textArea?: TextAreaProps;
    addButton?: InjectableCss;
    addRemove?: InjectableCss;
    sectionHead?: InjectableCss;
}

const enum fields {
    event = "event",
    date = "date",
    time = "time",
    location = "location",
    cost = "cost",
}

interface Registrant {
    [x: string]: string | number | undefined;
    firstName?: string;
    lastName?: string;
    company?: string;
    title?: string;
    phone?: number;
    email?: string;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    sessionData: state.context.session,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    userDatails: state.cbData.userDetails.value,
});

const mapDispatchToProps = {
    addEventRegistrationFormFields,
    loadUserDetails,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.event]: string;
        [fields.date]: string;
        [fields.time]: string;
        [fields.location]: string;
        [fields.cost]: string;
    };
}

export const eventRegistrationFormStyles: EventRegistrationFormStyles = {
    formWrapper: {
        css: css`
            [data-test-selector="registrant-head"] {
                + div {
                    display: none;
                }
            }
        `,
    },

    sectionHead: {
        css: css`
            font-weight: bold;
            padding: 9px;
            padding-left: 15px;
            margin-bottom: 25px;
            margin-top: 25px;
        `,
    },

    gridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > div[class^="FormFieldStyle"] {
                position: relative;
                width: 100%;
                > label {
                    font-size: 13px;
                    position: absolute;
                    z-index: 1;
                    padding-left: 5px;
                    background-color: #fff;
                    display: inline-block;
                    width: auto;
                    left: 20px;
                    min-height: 19px;
                }
            }
        `,
    },

    addRemove: {
        css: css`
            padding: 15px 0;
            display: flex;
        `,
    },

    addButton: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0;
            background: transparent;
            border: 0;
            color: #0f0f0f;
            margin-right: 15px;

            &:hover {
                background: transparent;
                color: #ef3742;
            }

            span {
                font-size: 18px;
                font-weight: bold;
                line-height: 24px;
                padding: 1px 7px 0 7px;
                border-radius: 7px;
                background: #ef3742;
                color: #fff;
                margin-right: 10px;

                svg {
                    background: #ef3742;
                    border-radius: 7px;
                    rect {
                        color: transparent;
                    }
                }
            }
        `,
    },

    sectionWrapper: {
        css: css`
            margin-bottom: 15px;
            div {
                line-height: 30px;
            }
        `,
    },

    sectionLabel: {
        css: css`
            text-transform: capitalize;
            font-weight: bold;
        `,
    },

    redAstick: {
        css: css`
            color: #ef3742;
        `,
    },

    section: {
        css: css`
            padding: 15px 0;
            div {
                line-height: 25px;
            }
        `,
    },

    registerSection: {
        css: css`
            padding: 15px 0;
            text-align: center;
        `,
    },
};

const styles = eventRegistrationFormStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

const EventRegistration: FC<Props> = ({
    id,
    fields,
    addEventRegistrationFormFields,
    loadUserDetails,
    sessionData,
    currentUserIsGuest,
    userDatails,
}) => {
    const [showForm, setForm] = React.useState(false);
    const [users, setUsers] = React.useState(new Array<Registrant>(1));
    const [removeBtn, setRemoveBtn] = React.useState(false);
    const [isUserArrayEmpty, setIsUserArrayEmpty] = React.useState(true);
    const obj: Registrant = {};
    const [billingComments, setBillingComments] = React.useState("");
    const [billingPo, setBillingPo] = React.useState("");
    const [paymentMethodSelect, setPaymentMethodSelect] = React.useState("");
    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType[]>(new Array(intialErrorFormState));
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);
    const [emailError, setEmailError] = React.useState(emailRequiredFieldMessage);
    const [phone, setPhone] = React.useState("");

    useEffect(() => {
        if (sessionData.isAuthenticated) {
            const loggedInUser = [...users];
            loadUserDetails();
            loggedInUser[0].firstName = sessionData.firstName;
            loggedInUser[0].lastName = sessionData.lastName;
            loggedInUser[0].email = sessionData.email;
            setUsers(loggedInUser);
            // validateHandler(input);
        }
    }, []);

    React.useEffect(() => {
        const loggedInUser = [...users];
        if (sessionData.isAuthenticated && userDatails && userDatails.length > 0) {
            loggedInUser[0].company = userDatails[0].companyName;
            loggedInUser[0].phone = userDatails[0].phoneNumber;
        }
    }, [userDatails]);

    React.useEffect(() => {
        const firstNameSelector: HTMLInputElement | null = document.querySelector('[name="firstName"]');
        const LastNameSelector: HTMLInputElement | null = document.querySelector('[name="lastName"]');
        const emailSelector: HTMLInputElement | null = document.querySelector('[name="email"]');
        const companySelector: HTMLInputElement | null = document.querySelector('[name="company"]');
        onLoadValidateHandler(firstNameSelector);
        onLoadValidateHandler(LastNameSelector);
        onLoadValidateHandler(companySelector);
        if (sessionData.isAuthenticated && emailSelector) {
            validateEmail(emailSelector.value);
            validateHandler(emailSelector);
        }
    }, [users]);

    const onLoadValidateHandler = (input: HTMLInputElement | null) => {
        if (sessionData.isAuthenticated && input) {
            validateHandler(input);
        }
    };

    if (isUserArrayEmpty) {
        for (let i = 0; i < users.length; i++) {
            users[i] = {};
        }
        setIsUserArrayEmpty(false);
    }

    const addRegistrantRow = () => {
        setUsers([...users, obj]);
        const errorFormState = {
            firstName: "",
            lastName: "",
            company: "",
            phone: "",
            email: "",
        };
        setFormErrors([...formErrors, errorFormState]);
        setRemoveBtn(true);
        setIsButtonDisabled(true);
    };

    const removeRegistrantRow = () => {
        setUsers(users => {
            const index = users.length - 1;
            if (index > 0) {
                const user = users.filter((item, j) => index !== j);
                if (index < 2) {
                    setRemoveBtn(false);
                }

                formErrors.pop();
                setIsButtonDisabled(validateSubmitDisabled([...formErrors]));
                setFormErrors([...formErrors]);
                return user;
            }
            return users;
        });
    };

    const updateUser = (input: HTMLInputElement, key: any) => {
        const usersList = [...users];
        usersList[key][input.name] = input.value;
        // users[key][input.name] = input.value;
        setUsers(usersList);
        validateHandler(input, key);
    };

    const updateEmailUser = (input: HTMLInputElement, key: any) => {
        const usersEmailList = [...users];
        usersEmailList[key][input.name] = input.value;
        validateEmail(input.value);
        validateHandler(input, key);
        setUsers(usersEmailList);
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validateSubmitDisabled = (errorsState: FormErrorsStateType[]) => {
        const errorCheck = errorsState?.filter(error => {
            if (error) {
                return Object.keys(error).length !== 0;
            }
            return null;
        });
        return errorCheck.length > 0;
    };

    const paymentMethodHandler = (event: any) => {
        const element = document.querySelector<HTMLSelectElement>('[name="paymentMethodSelect"]');
        if (element) {
            if (element.value === "Credit Card") {
                paymentMethodRemoveValidationHandler(event);
            } else if (element.value === "Purchase Order") {
                paymentMethodAddValidationHandler(event);
            } else {
                validateHandler(event);
            }
        }
    };

    const paymentMethodAddValidationHandler = (input: any, customValidation?: CallbackType) => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const element = document.querySelector<HTMLInputElement>('[name="billingPo"]');
        const validatorConfig = eventRegistrationValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);

        if (element) {
            if (element.value !== "") {
                delete formErrors[0][input.name as keyof FormErrorsStateType];
                delete formErrors[0]["billingPo" as keyof FormErrorsStateType];
                setFormErrors(formErrors);
            } else {
                formErrors[0] = {
                    ...formErrors[0],
                    [input.name as keyof FormErrorsStateType]: isValidate ? isValidate.errorMsg : isValidate,
                    ["billingPo" as keyof FormErrorsStateType]: isValidate ? isValidate.errorMsg : isValidate,
                };
                setFormErrors([...formErrors]);
            }
        }

        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const paymentMethodRemoveValidationHandler = (event: any) => {
        delete formErrors[0]["billingPo" as keyof FormErrorsStateType];
        delete formErrors[0]["paymentMethodSelect" as keyof FormErrorsStateType];
        setFormErrors([...formErrors]);
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const validateHandler = (
        input: HTMLInputElement | HTMLSelectElement,
        key?: number,
        customValidation?: CallbackType,
    ): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const validatorConfig = eventRegistrationValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);

        if (isValidate === undefined) {
            delete formErrors[key || 0][input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            formErrors[key || 0] = {
                ...formErrors[key || 0],
                [input.name as keyof FormErrorsStateType]: isValidate ? isValidate.errorMsg : isValidate,
            };
            setFormErrors([...formErrors]);
        }
        if (!validatorConfig.required && input.value === "") {
            delete formErrors[key || 0][input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }

        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const registerFormShow = () => {
        setForm(true);
        window.scrollTo(0, 0);
    };

    const billingPoHandler = (input: HTMLInputElement) => {
        setBillingPo(input.value);
        validateHandler(input);
    };

    const billingPOBlurHandler = (input: any) => {
        setBillingPo(input.value);
        paymentMethodHandler(input);
    };

    const paymentMethodSelectHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPaymentMethodSelect(event.currentTarget.value);
        paymentMethodHandler(event.currentTarget);
    };

    const billingCommentsHandler = (textarea: HTMLTextAreaElement) => {
        setBillingComments(textarea.value);
    };

    const updateTitleUser = (input: HTMLInputElement, key: any) => {
        const usersList = [...users];
        usersList[key][input.name] = input.value;
        // users[key][input.name] = input.value;
        setUsers(usersList);
    };

    const phoneChangeHandler = (input: HTMLInputElement, key: any) => {
        const usersList = [...users];
        usersList[key][input.name] = formatPhoneNumber(input.value);
        setUsers(usersList);
        validateHandler(input, key);
    };

    const RegisterHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        const addEventRegistrationParams: EventRegistrationModel = {
            type: "EventRegistration",
            eventID: "",
            eventName: fields.event,
            eventDate: fields.date,
            eventTime: fields.time,
            eventLocation: fields.location,
            eventFee: fields.cost,
            paymentType: paymentMethodSelect,
            PO: billingPo,
            comments: billingComments,
            firstName: users[0].firstName,
            lastName: users[0].lastName,
            email: users[0].email,
            company: users[0].company,
            phone: users[0].phone,
            title: users[0].title,
            AdditionalRegistrants: users,
        };
        addEventRegistrationFormFields({
            addEventRegistrationParams,
            onSuccess: () => {
                clearForm();
                window.location.href = "/ThankYou";
            },
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {/* Content */}
            <StyledWrapper>
                {showForm ? (
                    <>
                        <StyledWrapper>
                            <StyledWrapper {...CreateAccountExistingFormStyles.sectionHeading}>
                                {translate("Course/Seminar Information")}
                            </StyledWrapper>
                            <StyledWrapper {...styles.sectionWrapper}>
                                <StyledWrapper>
                                    <Typography {...styles.sectionLabel}>Event Name: </Typography>{" "}
                                    {parse(fields.event, parserOptions)}
                                </StyledWrapper>
                                <StyledWrapper>
                                    <Typography {...styles.sectionLabel}>Date/Time: </Typography>{" "}
                                    {parse(fields.date, parserOptions)} / {parse(fields.time, parserOptions)}
                                </StyledWrapper>
                                <StyledWrapper>
                                    <Typography {...styles.sectionLabel}>Location: </Typography>{" "}
                                    {parse(fields.location, parserOptions)}
                                </StyledWrapper>
                                <StyledWrapper>
                                    <Typography {...styles.sectionLabel}>Cost: </Typography> $
                                    {parse(fields.cost, parserOptions)} per attendee
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>

                        {/* Registrant and Billing Form */}
                        <StyledWrapper>
                            {/* Registrant Form */}
                            <StyledWrapper {...styles.formWrapper}>
                                {/* -- Heading -- */}
                                <StyledWrapper
                                    {...CreateAccountExistingFormStyles.sectionHeading}
                                    data-test-selector="registrant-head"
                                >
                                    Registrant Information
                                </StyledWrapper>

                                {/* -- Fields -- */}
                                {users.map((user, key) => (
                                    <>
                                        <StyledWrapper {...styles.sectionHead}>Registrant {key + 1}</StyledWrapper>
                                        <StyledWrapper key={key}>
                                            <GridContainer>
                                                <GridItem {...styles.gridItem}>
                                                    <TextField
                                                        value={user.firstName}
                                                        label={translate("First Name")}
                                                        onChange={e => updateUser(e.currentTarget, key)}
                                                        onBlur={e => validateHandler(e.currentTarget, key)}
                                                        name="firstName"
                                                        error={!!formErrors[key].firstName && formErrors[key].firstName}
                                                        iconProps={{
                                                            color: "danger",
                                                            src: formErrors[key].firstName ? AlertTriangle : undefined,
                                                        }}
                                                        required
                                                    />
                                                </GridItem>
                                                <GridItem {...styles.gridItem}>
                                                    <TextField
                                                        value={user.lastName}
                                                        label={translate("Last Name")}
                                                        onChange={e => updateUser(e.currentTarget, key)}
                                                        onBlur={e => validateHandler(e.currentTarget, key)}
                                                        name="lastName"
                                                        error={!!formErrors[key].lastName && formErrors[key].lastName}
                                                        iconProps={{
                                                            color: "danger",
                                                            src: formErrors[key].lastName ? AlertTriangle : undefined,
                                                        }}
                                                        required
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                            <GridContainer>
                                                <GridItem {...styles.gridItem}>
                                                    <TextField
                                                        value={user.company}
                                                        label={translate("Company")}
                                                        onChange={e => updateUser(e.currentTarget, key)}
                                                        onBlur={e => validateHandler(e.currentTarget, key)}
                                                        name="company"
                                                        error={!!formErrors[key].company && formErrors[key].company}
                                                        iconProps={{
                                                            color: "danger",
                                                            src: formErrors[key].company ? AlertTriangle : undefined,
                                                        }}
                                                        required
                                                    />
                                                </GridItem>
                                                <GridItem {...styles.gridItem}>
                                                    <TextField
                                                        value={user.title}
                                                        label={translate("Title")}
                                                        onChange={e => updateTitleUser(e.currentTarget, key)}
                                                        name="title"
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                            <GridContainer>
                                                <GridItem {...styles.gridItem}>
                                                    <TextField
                                                        value={user.phone}
                                                        label={translate("Phone")}
                                                        onChange={e => phoneChangeHandler(e.currentTarget, key)}
                                                        onBlur={e => validateHandler(e.currentTarget, key)}
                                                        name="phone"
                                                        error={!!formErrors[key].phone && formErrors[key].phone}
                                                        iconProps={{
                                                            color: "danger",
                                                            src: formErrors[key].phone ? AlertTriangle : undefined,
                                                        }}
                                                        required
                                                    />
                                                </GridItem>
                                                <GridItem {...styles.gridItem}>
                                                    <TextField
                                                        value={user.email}
                                                        label={translate("Email")}
                                                        onChange={e => updateEmailUser(e.currentTarget, key)}
                                                        onBlur={e => validateHandler(e.currentTarget, key)}
                                                        name="email"
                                                        error={!!formErrors[key].email && formErrors[key].email}
                                                        iconProps={{
                                                            color: "danger",
                                                            src: formErrors[key].email ? AlertTriangle : undefined,
                                                        }}
                                                        required
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                        </StyledWrapper>
                                    </>
                                ))}

                                {/* -- Button -- */}
                                <StyledWrapper {...styles.addRemove}>
                                    <Button {...styles.addButton} onClick={addRegistrantRow}>
                                        <span>+</span> Add another registrant
                                    </Button>
                                    {removeBtn ? (
                                        <Button onClick={removeRegistrantRow}>Remove registrant</Button>
                                    ) : null}
                                </StyledWrapper>
                            </StyledWrapper>

                            {/* Billing Information */}
                            <StyledWrapper>
                                {/* -- Heading -- */}
                                <StyledWrapper {...CreateAccountExistingFormStyles.sectionHeading}>
                                    Billing Information
                                </StyledWrapper>

                                {/* -- Fields -- */}
                                <GridContainer>
                                    <GridItem {...styles.gridItem}>
                                        <Select
                                            {...styles.paymentMethodSelect}
                                            label={translate("Select Payment Mode")}
                                            value={paymentMethodSelect}
                                            onChange={e => paymentMethodSelectHandler(e)}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            name="paymentMethodSelect"
                                            error={
                                                !!formErrors[0].paymentMethodSelect && formErrors[0].paymentMethodSelect
                                            }
                                            // iconProps={{
                                            //     color: "danger",
                                            //     src: formErrors.paymentMethodSelect ? AlertTriangle : undefined,
                                            // }}
                                            required
                                        >
                                            <option key={0} value="">
                                                Select Payment Method
                                            </option>
                                            <option key={1} value="Credit Card">
                                                Credit Card
                                            </option>
                                            <option key={2} value="Purchase Order">
                                                Purchase Order
                                            </option>
                                        </Select>
                                    </GridItem>
                                    <GridItem {...styles.gridItem}>
                                        <TextField
                                            value={billingPo}
                                            onChange={e => billingPoHandler(e.currentTarget)}
                                            onBlur={e => billingPOBlurHandler(e.currentTarget)}
                                            label={translate("PO#")}
                                            name="billingPo"
                                            error={
                                                paymentMethodSelect === "Credit Card"
                                                    ? !!formErrors[0].billingPo
                                                    : formErrors[0].billingPo
                                            }
                                            iconProps={{
                                                color: "danger",
                                                src:
                                                    paymentMethodSelect === "Purchase Order"
                                                        ? formErrors[0].billingPo
                                                            ? AlertTriangle
                                                            : undefined
                                                        : undefined,
                                            }}
                                            required={paymentMethodSelect === "Purchase Order"}
                                        />
                                    </GridItem>
                                </GridContainer>

                                {/* -- Information -- */}
                                <StyledWrapper {...styles.section}>
                                    <StyledWrapper>
                                        <Typography {...styles.sectionLabel}>Credit Card: </Typography>If not already
                                        file, we will connect you for credit card details. You will not be charged until
                                        after the event.
                                    </StyledWrapper>
                                    <StyledWrapper>
                                        <Typography {...styles.sectionLabel}>Purchase Order: </Typography>If your
                                        company has credit terms, you will be invoiced following the event.
                                    </StyledWrapper>
                                </StyledWrapper>

                                {/* -- Comments -- */}
                                <StyledWrapper>
                                    <TextArea
                                        {...styles.textArea}
                                        value={billingComments}
                                        onChange={e => billingCommentsHandler(e.currentTarget)}
                                        label={translate("Comments")}
                                        name="comments"
                                        placeholder="Comments or Special Instructions"
                                    />
                                </StyledWrapper>

                                {/* -- Register Button -- */}
                                <StyledWrapper {...styles.registerSection}>
                                    <Button onClick={RegisterHandler} disabled={isButtonDisabled}>
                                        {translate("Register Now")}
                                    </Button>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </>
                ) : (
                    <StyledWrapper>
                        <Zone contentId={id} zoneName="Heading" />

                        <StyledWrapper {...styles.sectionWrapper}>
                            <StyledWrapper>
                                <Typography {...styles.sectionLabel}>Event Name: </Typography>{" "}
                                {parse(fields.event, parserOptions)}
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography {...styles.sectionLabel}>Date/Time: </Typography>{" "}
                                {parse(fields.date, parserOptions)} / {parse(fields.time, parserOptions)}
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography {...styles.sectionLabel}>Location: </Typography>{" "}
                                {parse(fields.location, parserOptions)}
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography {...styles.sectionLabel}>Cost: </Typography> $
                                {parse(fields.cost, parserOptions)} per attendee
                            </StyledWrapper>
                        </StyledWrapper>

                        <Zone contentId={id} zoneName="Content" />
                        <StyledWrapper {...styles.registerSection}>
                            <Button onClick={registerFormShow}>{translate("Registration Open")}</Button>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(EventRegistration),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.event,
                editorTemplate: "TextField",
                displayName: "Event Name",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.date,
                editorTemplate: "TextField",
                displayName: "Date",
                defaultValue: "",
                placeholder: "Day, MM DD YYYY",
            },
            {
                fieldType: "Translatable",
                name: fields.time,
                editorTemplate: "TextField",
                displayName: "Time",
                defaultValue: "",
                placeholder: "08:00am - 10:00am",
            },
            {
                fieldType: "Translatable",
                name: fields.location,
                editorTemplate: "TextField",
                displayName: "Location",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.cost,
                editorTemplate: "TextField",
                displayName: "Cost",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
